import { HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { HttpService } from '@nebula-shared/data-access';
import { Observable, of, catchError, map, switchMap, tap } from 'rxjs';
import { AuthService } from './auth.service';
import { TokenBridgeService } from './token-bridge.service';
import { VaultTenantSessionBootstrapService } from './vault-tenant-session-bootstrap.service';
import { VAULT_SINGLE_TENANT_MODE } from './vault-active-tenant.util';
import { enrichVaultCompanyFromSimappeCompanies } from './vault-nebula-company.util';
import { isVaultLoginRoute } from './vault-account-blocked.util';

const COMPANY_ENDPOINT = '/simappe-admin/api/v1/company';
const JWT_SELECT_COMPANY = '/simappe-oauth2-server/api/v1/jwt-select-company';

export type VaultCompanyAutoEnterResult = {
  route: string;
  autoSelected: boolean;
  companyId?: string;
};

@Injectable({ providedIn: 'root' })
export class VaultCompanyBootstrapService {
  private readonly http = inject(HttpService);
  private readonly auth = inject(AuthService);
  private readonly tokenBridge = inject(TokenBridgeService);
  private readonly tenantBootstrap = inject(VaultTenantSessionBootstrapService);

  tryAutoEnterAfterLogin(): Observable<VaultCompanyAutoEnterResult> {
    if (!VAULT_SINGLE_TENANT_MODE) {
      return of({ route: '/layout/company', autoSelected: false });
    }

    const customerId = this.readCustomerId();
    if (customerId == null) {
      return of({ route: '/layout/company', autoSelected: false });
    }

    return this.readDatavaultCompanies(customerId).pipe(
      tap((companies) => {
        if (companies.length) {
          enrichVaultCompanyFromSimappeCompanies(null, companies);
        }
      }),
      switchMap((companies) => {
        if (companies.length !== 1) {
          return of({ route: '/layout/company', autoSelected: false });
        }
        const companyId = String(companies[0]?.id ?? '').trim();
        if (!companyId) {
          return of({ route: '/layout/company', autoSelected: false });
        }
        return this.applyCompanySelection(companyId).pipe(
          map(() => ({
            route: '/layout/products',
            autoSelected: true,
            companyId,
          })),
          catchError(() =>
            of({
              route: '/layout/products',
              autoSelected: false,
              companyId,
            }),
          ),
        );
      }),
    );
  }

  applyCompanySelection(companyId: string): Observable<void> {
    const headers = new HttpHeaders({
      AccessToken: this.auth.getToken()!,
      Accept: 'application/json',
    });

    return this.http
      .post<{ accessToken?: string; refreshToken?: string; expiresIn?: number }>(
        JWT_SELECT_COMPANY,
        {},
        { companyId },
        headers,
      )
      .pipe(
        switchMap((response) => {
          const content = response?.response?.content;
          const jwtToken = content?.accessToken;
          if (!jwtToken) {
            throw new Error('jwt-select-company sin accessToken');
          }

          const normalizedExpiresIn =
            content?.expiresIn && content.expiresIn > 1e12
              ? content.expiresIn - Date.now()
              : content?.expiresIn;

          this.auth.updateTokensAfterCompanySelection(
            jwtToken,
            content?.refreshToken,
            normalizedExpiresIn,
          );

          this.patchUserDataCompanyId(companyId, jwtToken, content?.refreshToken);
          this.tokenBridge.updateCookie();

          return this.auth.validateTokenAndGetSession().pipe(
            catchError(() => of(null)),
            switchMap(() =>
              isVaultLoginRoute()
                ? of(undefined)
                : this.tenantBootstrap.ensureSession$({ force: true }),
            ),
            tap(() => {
              if (typeof window !== 'undefined') {
                window.dispatchEvent(new CustomEvent('vault-session-updated'));
              }
            }),
            map(() => undefined),
          );
        }),
      );
  }

  private readDatavaultCompanies(customerId: number): Observable<Array<{ id?: string | number }>> {
    return this.http
      .getContent<Array<Record<string, unknown>>>(`${COMPANY_ENDPOINT}/read-customer`, {
        customerId: customerId.toString(),
      })
      .pipe(
        map((companies) =>
          (Array.isArray(companies) ? companies : []).filter((company) => {
            const customer = company['customer'] as { firstName?: string } | undefined;
            return customer?.firstName === 'Datavault';
          }),
        ),
        catchError(() => of([])),
      );
  }

  private readCustomerId(): number | null {
    try {
      const raw = localStorage.getItem('user_data');
      if (!raw) return null;
      const data = JSON.parse(raw) as { customerId?: number };
      return data?.customerId ?? null;
    } catch {
      return null;
    }
  }

  private patchUserDataCompanyId(
    companyId: string,
    accessToken?: string,
    refreshToken?: string,
  ): void {
    try {
      const raw = localStorage.getItem('user_data');
      if (!raw) return;
      const userData = JSON.parse(raw) as Record<string, unknown>;
      userData['companyId'] = companyId;
      if (accessToken) userData['token'] = accessToken;
      if (refreshToken) userData['refreshToken'] = refreshToken;
      localStorage.setItem('user_data', JSON.stringify(userData));
    } catch {
      /* ignore */
    }
  }
}
